import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Divider,
  Container,
  Paper,
  Alert,
  Stack
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useNavigate, useLocation } from 'react-router-dom';
import { addAction } from '../../service/Actions/addAction';
import dayjs from 'dayjs'; // Add this import

const primaryColor = '#339933'; // or '#beb28b' based on your config

const ActionAdd = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const route = location.state; 

  const [datepickerDate, setDatepickerDate] = useState(dayjs()); // Use dayjs() instead of new Date()
  const [addActionDateDue, setAddActionDateDue] = useState(null);
  const [actionDetailText, onChangeActionDetailText] = useState('');

  const [isSendingRequestNow, setIsSendingRequestNow] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const handleAddAction = async () => {
    setStatusMessage('Adding action now, please wait...');
    try {
      if (addActionDateDue && actionDetailText) {
        setIsSendingRequestNow(true);
        const requestBody = {
          dateDue: addActionDateDue,
          details: actionDetailText
        };
        const addActionRes = await addAction(requestBody);
        setIsSendingRequestNow(false);

        if (addActionRes.status === 200) {
          setAddActionDateDue(null);
          setStatusMessage('');
          onChangeActionDetailText('');

          if (addActionRes.data?.actionID) {
            // Redirect to action details page
            navigate(`/action/${addActionRes.data.actionID}`, {
              state: { fromScreen: 'ActionFormAdd' }
            });
          }
        } else {
          throw new Error("Error adding action");
        }
      } else {
        setIsSendingRequestNow(false);
        if (!addActionDateDue) {
          setStatusMessage('Please set a due date and try again.');
        } else if (!actionDetailText) {
          setStatusMessage('Please set action details and try again.');
        }
      }
    } catch (error) {
      setIsSendingRequestNow(false);
      setStatusMessage('Error adding action now, please try again');
    }
  };

  const handleDateChange = (date) => {
    if (date) {
      setDatepickerDate(date);
      const formattedDateString = date.format('YYYY-MM-DD'); // Use dayjs format method
      setAddActionDateDue(formattedDateString);
    }
  };

  const handleCancel = () => {
    navigate(-1); // Go back to previous page
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Add New Action
        </Typography>

        <Stack spacing={3}>
          {/* Due Date Section */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Due Date: {addActionDateDue || 'Please set a due date'}
            </Typography>
            
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en-gb'}>
              <DatePicker
                label="Set Due Date"
                value={datepickerDate}
                onChange={handleDateChange}
                renderInput={(params) => (
                  <TextField {...params} fullWidth margin="normal" />
                )}
                minDate={dayjs()} // Use dayjs() instead of new Date()
              />
            </LocalizationProvider>
          </Box>

          <Divider />

          {/* Details Section */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Details:
            </Typography>
            <TextField
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              placeholder="Enter action details..."
              value={actionDetailText}
              onChange={(e) => onChangeActionDetailText(e.target.value)}
              margin="normal"
            />
          </Box>

          <Divider />

          {/* Buttons Section */}
          <Box display="flex" justifyContent="space-between" gap={2}>
            <Button
              variant="outlined"
              color="secondary"
              disabled={isSendingRequestNow}
              onClick={handleCancel}
              sx={{ minWidth: 120 }}
            >
              CANCEL
            </Button>
            <Button
              variant="contained"
              disabled={isSendingRequestNow}
              onClick={handleAddAction}
              sx={{ 
                minWidth: 120,
                bgcolor: primaryColor,
                '&:hover': {
                  bgcolor: primaryColor,
                  opacity: 0.9
                }
              }}
            >
              ADD ACTION
            </Button>
          </Box>

          {/* Status Message */}
          {statusMessage && (
            <Alert 
              severity={statusMessage.includes('Error') ? 'error' : 'info'}
              sx={{ mt: 2 }}
            >
              {statusMessage}
            </Alert>
          )}
        </Stack>
      </Paper>
    </Container>
  );
};

export default ActionAdd;